import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject, pipe } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MappingService } from '@services/mapping.service';

import { MappingDependency } from '@classes/mapping/mapping-dependency';
import { DragDropItem } from '@interfaces/drag-drop-item';

@Component({
    selector: 'app-dependencies-list',
    templateUrl: './dependencies-list.component.html',
    styleUrls: ['./dependencies-list.component.scss']
})
export class DependenciesListComponent implements OnInit, OnChanges, OnDestroy {
    @Input() canEditMapping: boolean;
    @Input() dependencies: MappingDependency[] = [];
    @Input() isDense: boolean;
    @Input() label: string;
    @Output() listChanged = new EventEmitter<MappingDependency[]>();

    selDependencies: number[] = [];
    filteredDependencies: MappingDependency[] = [];
    editedItem: MappingDependency;

    filterForm: UntypedFormGroup;
    canEditDependencies: boolean;

    pipeCounter = 0;

    private dragged: MappingDependency[];

    private unsubscribeAll = new Subject<void>();

    constructor(
        private fb: UntypedFormBuilder,
        private mappingSrv: MappingService
    ) {
        this.canEditDependencies = this.mappingSrv.canEditDependencies;
    }

    ngOnInit(): void {
        this.initSubscriptions();
    }

    ngOnChanges(changes): void {
        this.initForm();
        this.initDependencies();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    isSelected(depId: number): boolean {
        return this.selDependencies.indexOf(depId) !== -1;
    }

    onSelect(depId: number): void {
        if (this.isSelected(depId)) {
            this.deselectDep(depId);
        } else {
            this.selectDep(depId);
        }
    }

    onEditStarted(item: MappingDependency): void {
        this.editedItem = item;
    }

    onEditFinished(editedItem?: MappingDependency): void {
        if (editedItem) {
            this.editedItem.label = editedItem.label;
            this.editedItem.description = editedItem.description;

            if (editedItem.properties) {
                this.editedItem.properties = [...editedItem.properties];
            }

            this.listChanged.emit(this.dependencies);
        }

        this.editedItem = null;
    }

    /**
     * Update the position up of a dependency in a group
     * @param index dependency index
     */
    moveUp(index: number): void {
        this.filteredDependencies.splice(index - 1, 0, this.filteredDependencies.splice(index, 1)[0]);
        this.filteredDependencies = this.filteredDependencies.map((item, index) => {
            item.displayOrder = index;
            return item;
        });
        this.pipeCounter--;
        this.listChanged.emit(this.filteredDependencies);
    }

    /**
     * Update the position down of a dependency in a group
     * @param index dependency index
     */
    moveDown(index: number): void {
        this.filteredDependencies.splice(index + 1, 0, this.filteredDependencies.splice(index, 1)[0]);
        this.filteredDependencies = this.filteredDependencies.map((item, index) => {
            item.displayOrder = index;
            return item;
        });
        this.pipeCounter++;
        this.listChanged.emit(this.filteredDependencies);
    }

    /**
     * By default, data/elements cannot be dropped in other elements.
     * Prevent the default handling of the element.
     * @param event DragEvent
     */
    allowDrop(event: DragEvent) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    }

    /**
     * Specifies what data to be dragged.
     * Update draggedItemChanged subject and isDragging property
     * @param event DragEvent
     * @param selectedItem TreeItem
     */
    onDrag(event: DragEvent, selectedItem: MappingDependency): void {
        event.dataTransfer.setData('data', selectedItem.id.toString());
        event.dataTransfer.dropEffect = "move";

        let selectedItems: MappingDependency[] = [selectedItem];

        if (this.selDependencies.length) {
            selectedItems = this.dependencies.filter(a => this.selDependencies.find(id => id === a.id));
        }

        this.dragged = selectedItems;
        this.mappingSrv.dragItem({ items: selectedItems, type: 'dep' });
    }

    /**
     * When the dragged data is dropped.
     * Update dependencies depending of draggedItem type
     * draggedItem is updated with subscriptions
     * @param event DragEvent
     * @param toItem TreeItem
     */
    onDrop(event: DragEvent): void {
        const draggedItem: DragDropItem = this.mappingSrv.dragged;

        switch (draggedItem.type) {
            case 'dep':
                this.dropDep(draggedItem.items as MappingDependency[]);
                break;
            case 'tree':
            default:
                this.mappingSrv.droppedItemChanged.next(false);
        }

        event.preventDefault();
    }

    /**
     * Add dropped items to dependencies list and emit changes
     * @param draggedItems TreeItems
     */
    private dropDep(draggedItems: MappingDependency[]): void {
        if (!this.dependencies.find(d => draggedItems.find(i => i === d))) {
            this.dependencies = [...this.dependencies, ...draggedItems];
            this.filteredDependencies = [...this.dependencies];

            // Update subject to remove dropped items from the original list
            this.mappingSrv.droppedItemChanged.next(true);
            // Send new list to parent
            this.listChanged.emit(this.dependencies);
        }
    }

    private selectDep(depId: number): void {
        if (!this.isSelected(depId)) {
            this.selDependencies.push(depId);
        }
    }

    private deselectDep(depId: number): void {
        this.selDependencies = this.selDependencies.filter(id => id !== depId);
    }

    private initDependencies(): void {
        if (this.isDense) {
            const isNotOrdered = this.dependencies.every(dependency => dependency.displayOrder === 0);
            if (isNotOrdered) {
                this.dependencies = this.dependencies.map((dependency, index) => {
                    dependency.displayOrder = index;
                    return dependency;
                });
            }
        } else {
            this.dependencies = this.dependencies ? this.dependencies.sort((a, b) => a.code.toLowerCase() < b.code.toLowerCase() ? -1 : 1) : [];
        }

        this.filteredDependencies = [...this.dependencies];
    }

    private initForm(): void {
        this.filterForm = this.fb.group({
            filter: [''],
            selectAll: [false]
        });

        this.filterForm.controls.filter.valueChanges
            .subscribe((value: string) => {
                this.filteredDependencies = this.dependencies
                    .filter(d => d.code.toLowerCase().includes(value.trim().toLowerCase()));
            });

        this.filterForm.controls.selectAll.valueChanges
            .subscribe((selectAll: boolean) => {
                this.filteredDependencies.map(a => {
                    if (selectAll) {
                        this.selectDep(a.id);
                    } else {
                        this.deselectDep(a.id);
                    }
                });
            });
    }

    private initSubscriptions(): void {
        this.mappingSrv.droppedItemChanged
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(hasDrop => {
                // Filter list to remove dropped items
                if (this.dragged && hasDrop) {
                    this.dependencies = this.dependencies.filter(i => !this.dragged.find(d => d === i));
                    this.filteredDependencies = [...this.dependencies];

                    this.selDependencies = [];
                    this.dragged = null;
                    this.listChanged.emit(this.dependencies);
                }
            });
    }
}
